/**
 * «Интерьеры: оценки» (`/lib/interiors`): итоги голосования сотрудников за интерьеры (спека §6).
 * Ссылка на ленту для сотрудников, фильтр по человеку (`?user=`), таблица категорий по доле 👍 и
 * два топа фото. С фильтром топы — личные лайки/дизлайки человека (от 1 голоса), без него — от 2.
 */
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useSearchParams } from "react-router";

import { btn, Empty, ErrorLine, Loading, Panel, Segmented } from "@/components/trends/ui";
import { SHARE_URL, sharePct, sortByShare, type InteriorStats, type TopItem } from "@/lib/interiors";
import { loadStats } from "@/lib/interiorsApi";
import { thumbUrl } from "@/lib/media";
import { retryQuery } from "@/lib/trends";

export function InteriorStatsPage() {
  const [params, setParams] = useSearchParams();
  const raw = Number(params.get("user"));
  const userId = Number.isInteger(raw) && raw > 0 ? raw : undefined;
  const stats = useQuery({
    queryKey: ["interiors", "stats", userId ?? null],
    queryFn: () => loadStats(userId),
    retry: retryQuery,
  });
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(SHARE_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* буфер недоступен — ссылка видна текстом, скопируют руками */
    }
  };

  const pickUser = (v: string) =>
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        if (v === "all") next.delete("user");
        else next.set("user", v);
        return next;
      },
      { replace: true },
    );

  return (
    <div className="grid gap-4 p-5">
      <div>
        <Link to="/lib" className="text-[12.5px] text-fg-muted hover:text-fg">
          ← Библиотеки
        </Link>
        <h1 className="mt-1 text-[18px] font-semibold tracking-[-0.01em] text-fg">Интерьеры: оценки</h1>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-[12.5px] text-fg-muted">
          <span>Ссылка для сотрудников:</span>
          <code className="tnum text-fg">{SHARE_URL}</code>
          <button type="button" className={btn} onClick={copy}>
            {copied ? "скопировано" : "скопировать"}
          </button>
        </div>
      </div>

      {stats.isPending ? (
        <Loading />
      ) : stats.isError ? (
        <ErrorLine error={stats.error} />
      ) : (
        <StatsBody data={stats.data} userId={userId} onPick={pickUser} />
      )}
    </div>
  );
}

function StatsBody({ data, userId, onPick }: { data: InteriorStats; userId: number | undefined; onPick: (v: string) => void }) {
  const { categories, raters, top_works, top_fails } = data;
  const empty = categories.every((c) => c.votes === 0) && raters.length === 0;
  return (
    <>
      <Segmented<string>
        label="сотрудник"
        value={userId ? String(userId) : "all"}
        onPick={onPick}
        options={[
          { value: "all", label: "все" },
          ...raters.map((r) => ({ value: String(r.user_id), label: `${r.name} (${r.votes})` })),
        ]}
      />
      {empty ? (
        <Panel annot="оценки">
          <Empty>Голосов пока нет — отправьте ссылку сотрудникам</Empty>
        </Panel>
      ) : (
        <>
          <Panel title="Категории" annot="по доле 👍">
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left text-fg-muted">
                    <th className="p-2 font-medium">категория</th>
                    <th className="p-2 text-right font-medium">в пуле</th>
                    <th className="p-2 text-right font-medium">голосов</th>
                    <th className="p-2 text-right font-medium">👍</th>
                    <th className="p-2 text-right font-medium">👎</th>
                    <th className="p-2 text-right font-medium">доля 👍</th>
                    <th className="p-2 text-right font-medium">человек</th>
                  </tr>
                </thead>
                <tbody>
                  {sortByShare(categories).map((c) => (
                    <tr key={c.tag_id} className="border-t border-border">
                      <td className="p-2 text-fg">{c.name}</td>
                      <td className="tnum p-2 text-right">{c.pool}</td>
                      <td className="tnum p-2 text-right">{c.votes}</td>
                      <td className="tnum p-2 text-right">{c.works}</td>
                      <td className="tnum p-2 text-right">{c.fails}</td>
                      <td className="tnum p-2 text-right">{sharePct(c)}</td>
                      <td className="tnum p-2 text-right">{c.raters}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
          <TopGrid title="Больше всего 👍" items={top_works} />
          <TopGrid title="Больше всего 👎" items={top_fails} />
        </>
      )}
    </>
  );
}

function TopGrid({ title, items }: { title: string; items: TopItem[] }) {
  return (
    <Panel title={title}>
      {items.length === 0 ? (
        <Empty>Пока нет фото</Empty>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-3 p-3">
          {items.map((it) => (
            <Link key={it.id} to={`/lib/ref/${it.id}`} className="block min-w-0">
              <img
                src={thumbUrl(it.asset_path)}
                alt={it.tag_name}
                loading="lazy"
                className="aspect-[3/4] w-full rounded-md object-cover"
              />
              <div className="mt-1 truncate text-[12px] text-fg">{it.tag_name}</div>
              <div className="tnum text-[12px] text-fg-muted">
                👍 {it.works} · 👎 {it.fails}
              </div>
            </Link>
          ))}
        </div>
      )}
    </Panel>
  );
}
